import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { push } from 'connected-react-router';
import { useRouteMatch } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { RoleNames, conventionStatus, distributorType } from 'utils/constants';
import { parseDate } from 'utils/helpers';
import { useMatchAtLeastOneRole } from 'utils/hooks';
import { texts } from 'utils/languages/fr';

import {
    MenuDeleteUser,
    MenuDetails,
    MenuEdit,
    MenuSendMessage,
    MenuSubstitute,
} from 'components/ActionsMenu/actions';
import { ActionsMenu, DistributorAlertCircle, StyledTableRow } from 'components';
import { TableCell } from '@mui/material';

const getConventionStatus = (user, company) => {
    const isCommercialisateur =
        user?.subscriptions?.[0]?.role?.name === RoleNames.ADMIN_CIF ||
        user?.subscriptions?.[0]?.role?.name === RoleNames.PSI;

    if (isCommercialisateur) {
        return company.conventions.some(
            convention => convention.status === conventionStatus.FULLY_SIGNED
        )
            ? 'completed'
            : company.status || '';
    } else {
        return company.status || '';
    }
};
const DeleteUserItem = React.forwardRef((props, ref) => <MenuDeleteUser ref={ref} {...props} />);
const ImpersonateItem = React.forwardRef((props, ref) => <MenuSubstitute ref={ref} {...props} />);
const EditDistributorItem = React.forwardRef((props, ref) => <MenuEdit ref={ref} {...props} />);
const MessageItem = React.forwardRef((props, ref) => <MenuSendMessage ref={ref} {...props} />);
const DetailsItem = React.forwardRef((props, ref) => <MenuDetails ref={ref} {...props} />);

const DistributorRow = ({
    company: {
        0: { user, ...company },
        nrOngoingSubscriptions,
        nrFinishedSubscriptions,
    },
}) => {
    const { path } = useRouteMatch();
    const dispatch = useDispatch();

    const userIsAdminOrSignatoryServicer = useMatchAtLeastOneRole([
        RoleNames.SERVICER_ADMIN,
        RoleNames.SERVICER_SIGNATORY,
    ]);
    const createdDate = useMemo(() => parseDate(user?.createdAt), [user]);
    const distributorStatus = useMemo(() => getConventionStatus(user, company), [user, company]);

    const handleEditDistributor = useCallback(
        () => dispatch(push(`${path}/edit/${company.id}`)),
        [dispatch, path, company]
    );

    return (
        <StyledTableRow>
            <TableCell>
                <DistributorAlertCircle status={distributorStatus} />
            </TableCell>
            <TableCell sx={theme => ({ color: theme.palette.common.black })}>
                {company.corporateName}
            </TableCell>
            <TableCell>{createdDate || ''}</TableCell>
            <TableCell>{nrOngoingSubscriptions || 0}</TableCell>
            <TableCell>{nrFinishedSubscriptions || 0}</TableCell>
            <TableCell>
                <ActionsMenu>
                    {userIsAdminOrSignatoryServicer &&
                        +nrOngoingSubscriptions === 0 &&
                        +nrFinishedSubscriptions === 0 && (
                            <DeleteUserItem id={company.id} type="distributor" />
                        )}
                    {user.emailConfirmed && userIsAdminOrSignatoryServicer && (
                        <ImpersonateItem
                            user={user}
                            connectAs="distributor"
                            originalPath="/distributors"
                            followupPath="/homepage"
                            itemText={texts.IMPERSONATE_DISTRIBUTOR}
                        />
                    )}
                    {userIsAdminOrSignatoryServicer && <MessageItem receiver={user} />}

                    <DetailsItem path={`${path}/details/${company.id}`} />
                    {company.type !== distributorType.STANDARD &&
                        userIsAdminOrSignatoryServicer && (
                            <EditDistributorItem onClick={handleEditDistributor} />
                        )}
                </ActionsMenu>
            </TableCell>
        </StyledTableRow>
    );
};

DistributorRow.propTypes = {
    company: PropTypes.object,
};

export default DistributorRow;
